'use client';

import { Download, Check } from 'lucide-react';

type QueueItem = { chapterId: number; chapterName: string };

type Props = {
  downloadingName: string | null;
  downloadProgress: { current: number; total: number } | null;
  downloadPhase?: 'page' | 'chapter' | null;
  fila: QueueItem[];
};

export default function FilaDownloadPanel({
  downloadingName,
  downloadProgress,
  downloadPhase = null,
  fila,
}: Props) {
  const total = (downloadingName !== null ? 1 : 0) + fila.length;
  if (total < 1) return null;

  const isPreparingPage = downloadPhase === 'page';
  const percent =
    downloadProgress && downloadProgress.total > 0
      ? Math.min(100, Math.round((downloadProgress.current / downloadProgress.total) * 100))
      : 0;

  return (
    <div className="fixed bottom-20 right-4 z-[90] w-[min(320px,calc(100vw-2rem))] rounded-2xl border border-white/10 bg-black/70 backdrop-blur-xl overflow-hidden md:bottom-6">
      <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
        <div className="flex min-w-0 gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500/20 text-orange-400">
            <Download size={18} strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">Downloads</p>
            <p className="text-xs text-white/50">
              {downloadingName
                ? isPreparingPage
                  ? 'Preparando página offline...'
                  : 'Baixando capítulo...'
                : `${fila.length} na fila`}
            </p>
          </div>
        </div>
        <span className="shrink-0 tabular-nums text-xs font-medium text-white/40">
          {fila.length + (downloadingName ? 1 : 0)} {fila.length + (downloadingName ? 1 : 0) === 1 ? 'item' : 'itens'}
        </span>
      </div>

      <div className="space-y-4 px-4 pb-4 pt-3">
        {downloadingName !== null && (
          <div className="space-y-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm text-white/80" title={downloadingName}>
                {downloadingName}
              </span>
              {!isPreparingPage && (
                <span className="shrink-0 tabular-nums text-sm font-semibold text-green-400">
                  {percent}%
                </span>
              )}
            </div>
            {isPreparingPage ? (
              <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                <div className="h-full w-1/3 rounded-full bg-orange-500 animate-pulse" />
              </div>
            ) : (
              <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-green-500 transition-all duration-300 ease-out"
                  style={{ width: `${percent}%` }}
                />
              </div>
            )}
          </div>
        )}

        {fila.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wider text-white/40">
              Próximos
            </p>
            <ul className="max-h-32 space-y-1.5 overflow-y-auto">
              {fila.map((item, index) => (
                <li
                  key={item.chapterId}
                  className="flex items-center gap-2 truncate text-sm text-white/70"
                  title={item.chapterName}
                >
                  <span className="shrink-0 font-mono text-xs text-white/40">
                    {downloadingName ? index + 2 : index + 1}.
                  </span>
                  {item.chapterName}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
